import { useRef, useState } from "react";
import styles from "./BookingForm.module.css";
import Icon from "./Icons";
import { booking } from "../data/content";
import useSiteSettings from "../hooks/useSiteSettings";

const EMPTY = {
  name: "",
  phone: "",
  concern: "",
  visitType: "Clinic visit",
  date: "",
  slot: "",
  notes: "",
};

function validate(values) {
  const errors = {};
  if (!values.name.trim()) errors.name = "Enter your name so I know who I'm replying to.";
  const digits = values.phone.replace(/\D/g, "");
  if (!values.phone.trim()) errors.phone = "Enter a phone number I can reach you on.";
  else if (digits.length < 10) errors.phone = "That number looks too short — include the full 10 digits.";
  if (!values.concern) errors.concern = "Pick the closest match so I can prepare.";
  if (!values.slot) errors.slot = "Choose a time of day that suits you.";
  return errors;
}

/** Composes the request into a readable message rather than a data dump. */
function composeMessage(v) {
  const lines = [
    `Hello Dr. Sharma, I'd like to request an appointment.`,
    ``,
    `Name: ${v.name.trim()}`,
    `Phone: ${v.phone.trim()}`,
    `Concern: ${v.concern}`,
    `Type: ${v.visitType}`,
    `Preferred: ${v.date ? v.date : "Any day"} — ${v.slot}`,
  ];
  if (v.notes.trim()) lines.push(``, `Notes: ${v.notes.trim()}`);
  return lines.join("\n");
}

export default function BookingForm() {
  const { doctor } = useSiteSettings();
  const [values, setValues] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [sent, setSent] = useState(false);
  const summaryRef = useRef(null);

  const set = (field) => (e) => {
    const value = e.target.value;
    setValues((v) => ({ ...v, [field]: value }));
    // clear the error as soon as the field becomes valid again
    if (errors[field]) {
      setErrors((prev) => {
        const next = validate({ ...values, [field]: value });
        const copy = { ...prev };
        if (!next[field]) delete copy[field];
        return copy;
      });
    }
  };

  const onBlur = (field) => () => {
    setTouched((t) => ({ ...t, [field]: true }));
    const next = validate(values);
    setErrors((prev) => ({ ...prev, ...(next[field] ? { [field]: next[field] } : {}) }));
    if (!next[field]) {
      setErrors((prev) => {
        const copy = { ...prev };
        delete copy[field];
        return copy;
      });
    }
  };

  const handleSubmit = (e, channel) => {
    e.preventDefault();
    const next = validate(values);
    setErrors(next);
    setTouched({ name: true, phone: true, concern: true, slot: true });

    if (Object.keys(next).length) {
      // move focus to the summary so the errors are announced.
      // setTimeout rather than rAF: rAF does not run in a background
      // tab, which would silently skip focus management.
      setTimeout(() => summaryRef.current?.focus(), 0);
      return;
    }

    const body = composeMessage(values);
    if (channel === "email") {
      window.location.href = `mailto:${doctor.email}?subject=${encodeURIComponent(
        "Appointment request"
      )}&body=${encodeURIComponent(body)}`;
    } else {
      window.open(
        `https://wa.me/${doctor.whatsapp}?text=${encodeURIComponent(body)}`,
        "_blank",
        "noopener,noreferrer"
      );
    }
    setSent(true);
  };

  const errorList = Object.entries(errors);
  const invalid = (f) => touched[f] && errors[f];

  return (
    <form className={styles.form} onSubmit={(e) => handleSubmit(e, "whatsapp")} noValidate>
      <div className={styles.head}>
        <h2 className={styles.title}>{booking.title}</h2>
        <p className={styles.note}>{booking.note}</p>
      </div>

      {errorList.length > 0 && (
        <div
          className={styles.summary}
          role="alert"
          tabIndex={-1}
          ref={summaryRef}
        >
          <strong>Check these before sending:</strong>
          <ul>
            {errorList.map(([field, msg]) => (
              <li key={field}>
                <a href={`#bk-${field}`}>{msg}</a>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className={styles.row}>
        <div className={styles.field}>
          <label htmlFor="bk-name">
            Your name <span aria-hidden="true">*</span>
          </label>
          <input
            id="bk-name"
            name="name"
            type="text"
            autoComplete="name"
            value={values.name}
            onChange={set("name")}
            onBlur={onBlur("name")}
            aria-required="true"
            aria-invalid={invalid("name") ? "true" : undefined}
            aria-describedby={invalid("name") ? "bk-name-err" : undefined}
            className={invalid("name") ? styles.inputBad : ""}
          />
          {invalid("name") && (
            <p className={styles.err} id="bk-name-err">
              {errors.name}
            </p>
          )}
        </div>

        <div className={styles.field}>
          <label htmlFor="bk-phone">
            Phone <span aria-hidden="true">*</span>
          </label>
          <input
            id="bk-phone"
            name="phone"
            type="tel"
            inputMode="tel"
            autoComplete="tel"
            placeholder="10-digit mobile"
            value={values.phone}
            onChange={set("phone")}
            onBlur={onBlur("phone")}
            aria-required="true"
            aria-invalid={invalid("phone") ? "true" : undefined}
            aria-describedby={invalid("phone") ? "bk-phone-err" : undefined}
            className={invalid("phone") ? styles.inputBad : ""}
          />
          {invalid("phone") && (
            <p className={styles.err} id="bk-phone-err">
              {errors.phone}
            </p>
          )}
        </div>
      </div>

      <div className={styles.field}>
        <label htmlFor="bk-concern">
          What's bothering you? <span aria-hidden="true">*</span>
        </label>
        <select
          id="bk-concern"
          name="concern"
          value={values.concern}
          onChange={set("concern")}
          onBlur={onBlur("concern")}
          aria-required="true"
          aria-invalid={invalid("concern") ? "true" : undefined}
          aria-describedby={invalid("concern") ? "bk-concern-err" : undefined}
          className={invalid("concern") ? styles.inputBad : ""}
        >
          <option value="">Select the closest match</option>
          {booking.concerns.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
        {invalid("concern") && (
          <p className={styles.err} id="bk-concern-err">
            {errors.concern}
          </p>
        )}
      </div>

      <fieldset className={styles.fieldset}>
        <legend>Where would you like to be seen?</legend>
        <div className={styles.choices}>
          {booking.visitTypes.map((t) => (
            <label key={t} className={styles.choice}>
              <input
                type="radio"
                name="visitType"
                value={t}
                checked={values.visitType === t}
                onChange={set("visitType")}
              />
              <span>{t}</span>
            </label>
          ))}
        </div>
      </fieldset>

      <div className={styles.row}>
        <div className={styles.field}>
          <label htmlFor="bk-date">Preferred date</label>
          <input
            id="bk-date"
            name="date"
            type="date"
            value={values.date}
            onChange={set("date")}
            min={new Date().toISOString().split("T")[0]}
          />
          <p className={styles.hint}>Optional — leave blank for the soonest slot.</p>
        </div>

        <div className={styles.field}>
          <label htmlFor="bk-slot">
            Time of day <span aria-hidden="true">*</span>
          </label>
          <select
            id="bk-slot"
            name="slot"
            value={values.slot}
            onChange={set("slot")}
            onBlur={onBlur("slot")}
            aria-required="true"
            aria-invalid={invalid("slot") ? "true" : undefined}
            aria-describedby={invalid("slot") ? "bk-slot-err" : undefined}
            className={invalid("slot") ? styles.inputBad : ""}
          >
            <option value="">Select a slot</option>
            {booking.slots.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
          {invalid("slot") && (
            <p className={styles.err} id="bk-slot-err">
              {errors.slot}
            </p>
          )}
        </div>
      </div>

      <div className={styles.field}>
        <label htmlFor="bk-notes">Anything I should know beforehand?</label>
        <textarea
          id="bk-notes"
          name="notes"
          rows={3}
          value={values.notes}
          onChange={set("notes")}
          placeholder="How long it's been going on, any scans or surgery, what makes it worse."
        />
      </div>

      <div className={styles.actions}>
        <button type="submit" className={styles.send}>
          <Icon name="whatsapp" size={20} />
          Send request on WhatsApp
        </button>
        <button
          type="button"
          className={styles.alt}
          onClick={(e) => handleSubmit(e, "email")}
        >
          Send by email instead
        </button>
      </div>

      <p className={styles.privacy} aria-live="polite">
        {sent
          ? "Your message is ready in WhatsApp — press send there and I'll confirm your slot."
          : booking.privacy}
      </p>
    </form>
  );
}
